// AddDeal.jsx
import React, { useState, useEffect } from "react";
import { apiGet, apiPost } from "../../utils/apiFetch";
import apiPath from "../../utils/pathObj";

// --- Icons ---
const CloseIcon = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>;
const InfoIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>;
const UserIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>;
const BuildingIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></svg>;
const ChevronDownIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>;
const PlusIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>;

// --- Reusable Field Components ---
const FormField = ({ label, required, tooltip, children }) => (
  <div className="mb-4">
    <label className="flex items-center text-sm text-gray-600 font-medium mb-1.5">
      {label} {required && <span className="text-red-500 ml-0.5">*</span>}
      {tooltip && <span className="ml-1 text-gray-400 cursor-help" title={tooltip}><InfoIcon /></span>}
    </label>
    {children}
  </div>
);

const Input = ({ type = "text", placeholder, value, onChange, name, icon }) => (
  <div className="relative">
    {icon && <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">{icon}</div>}
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      autoComplete="off"
      className={`w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 placeholder-gray-400 ${icon ? "pl-9" : ""}`}
    />
  </div>
);

const Select = ({ name, value, onChange, options, placeholder }) => (
  <div className="relative">
    <select
      name={name}
      value={value}
      onChange={onChange}
      className="w-full border border-gray-300 rounded px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 appearance-none cursor-pointer pr-8"
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
    <div className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
      <ChevronDownIcon />
    </div>
  </div>
);

// --- Fallback pipeline stages (used if API returns nothing) ---
const FALLBACK_STAGES = [
  { id: "1", label: "Qualified" },
  { id: "2", label: "Contact Made" },
  { id: "3", label: "Prospect Qualified" },
  { id: "4", label: "Needs Defined" },
  { id: "5", label: "Proposal Made" },
  { id: "6", label: "Negotiations Started" },
];

// --- Main Component ---
function AddDeal({ onClose, onSave, pipelineId }) {
  const [formData, setFormData] = useState({
    title: "",
    value: "",
    currency: "USD",
    stageId: "", // will be set once stages load
    labels: "",
    expectedCloseDate: "",
    owner: "current_user",
    sourceChannel: "",
    sourceChannelId: "",
    personName: "",
    phone: "",
    phoneType: "work",
    email: "",
    emailType: "work",
    organizationName: "",
  });

  const [stages, setStages] = useState(FALLBACK_STAGES);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // ─── Load stages for the active pipeline ────────────
  useEffect(() => {
    (async () => {
      try {
        const statusRes = await apiGet(apiPath.pipedriveStatus, {});
        if (statusRes?.data?.data?.connected !== true) {
          setFormData((prev) => ({ ...prev, stageId: FALLBACK_STAGES[0].id }));
          return;
        }

        const params = pipelineId ? { pipeline_id: pipelineId } : {};
        const res = await apiGet(apiPath.pipedriveStages, params);
        let list = (res?.data?.data || [])
          .map((s) => ({
            id: String(s.id),
            label: s.name,
            order: s.order_nr,
            pipelineId: s.pipeline_id != null ? String(s.pipeline_id) : "",
          }))
          .sort((a, b) => a.order - b.order);
        if (pipelineId) {
          const filtered = list.filter((s) => String(s.pipelineId) === String(pipelineId));
          if (filtered.length) list = filtered;
        }
        if (list.length) {
          setStages(list);
          setFormData((prev) => ({ ...prev, stageId: list[0].id }));
        } else {
          setFormData((prev) => ({ ...prev, stageId: FALLBACK_STAGES[0].id }));
        }
      } catch {
        setFormData((prev) => ({ ...prev, stageId: FALLBACK_STAGES[0].id }));
      }
    })();
  }, [pipelineId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const currentStageIndex = stages.findIndex((s) => s.id === formData.stageId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    if (!formData.title.trim()) {
      setError("Deal title is required.");
      setIsSubmitting(false);
      return;
    }

    const payload = {
      title: formData.title.trim(),
      value: formData.value ? parseFloat(formData.value) : undefined,
      currency: formData.currency,
      stage_id: formData.stageId ? parseInt(formData.stageId, 10) : undefined,
      pipeline_id: pipelineId ? Number(pipelineId) : undefined,
      expected_close_date: formData.expectedCloseDate || undefined,
      person: formData.personName
        ? {
            name: formData.personName,
            email: formData.email || undefined,
            phone: formData.phone || undefined,
          }
        : undefined,
      organization: formData.organizationName
        ? { name: formData.organizationName }
        : undefined,
      owner_id:
        formData.owner === "current_user" ? undefined : formData.owner || undefined,
    };

    try {
      const res = await apiPost(apiPath.pipedriveDeals, payload);
      if (res?.data?.success === false) {
        setError(res?.data?.message || "Failed to create deal");
        return;
      }
      const created = res?.data?.data;
      if (!created?.id) {
        setError("Deal was not created (no id returned). Check Pipedrive connection.");
        return;
      }
      if (onSave) onSave(created);
      if (onClose) onClose();
    } catch (err) {
      console.error("Create deal failed:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to create deal"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex justify-end">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[1px]" onClick={onClose}></div>

      <div className="relative w-full max-w-4xl bg-white h-full shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">Add deal</h2>
          <button onClick={onClose} className="p-1 text-gray-500 hover:bg-gray-100 rounded-full transition-colors">
            <CloseIcon />
          </button>
        </div>

        {/* Scrollable Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="flex gap-8">
            {/* LEFT: Deal Details */}
            <div className="w-1/2">
              <FormField label="Contact person" tooltip="The person this deal is associated with.">
                <Input name="personName" placeholder="Search or add a person" value={formData.personName} onChange={handleChange} icon={<UserIcon />} />
              </FormField>

              <FormField label="Organization" tooltip="The company this deal belongs to.">
                <Input name="organizationName" placeholder="Search or add an organization" value={formData.organizationName} onChange={handleChange} icon={<BuildingIcon />} />
              </FormField>

              <FormField label="Title" required>
                <Input name="title" placeholder="e.g., Acme Corp - Website Redesign" value={formData.title} onChange={handleChange} />
              </FormField>

              <FormField label="Value" tooltip="The monetary value of the deal.">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input name="value" type="number" placeholder="0.00" value={formData.value} onChange={handleChange} />
                  </div>
                  <div className="w-32">
                    <Select
                      name="currency"
                      value={formData.currency}
                      onChange={handleChange}
                      options={[
                        { value: "USD", label: "USD ($)" },
                        { value: "EUR", label: "EUR (€)" },
                        { value: "INR", label: "INR (₹)" },
                      ]}
                    />
                  </div>
                </div>
                <button type="button" className="text-sm text-blue-600 hover:underline mt-1 block ml-auto">Add products</button>
              </FormField>

              {/* Pipeline Stage — chevrons from live stages */}
              <FormField label="Pipeline stage">
                <div className="flex items-center h-8 w-full">
                  {stages.map((stage, idx) => {
                    const isFilled = idx <= currentStageIndex;
                    const isLast = idx === stages.length - 1;
                    return (
                      <div
                        key={stage.id}
                        onClick={() => setFormData((prev) => ({ ...prev, stageId: stage.id }))}
                        title={stage.label}
                        className="relative cursor-pointer h-8 flex-1"
                        style={{
                          marginLeft: idx === 0 ? 0 : "-10px",
                          zIndex: stages.length - idx,
                        }}
                      >
                        <div
                          className={`absolute inset-0 ${isFilled ? "bg-[#317e3e]" : "bg-gray-200"} transition-colors`}
                          style={{
                            clipPath: isLast
                              ? "polygon(0 0, 100% 0, 100% 100%, 0 100%, 10px 50%)"
                              : "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%, 10px 50%)",
                          }}
                        />
                        <div className="absolute inset-0 hover:bg-black/5" />
                      </div>
                    );
                  })}
                </div>
              </FormField>

              <FormField label="Label" tooltip="Categorize this deal.">
                <Input name="labels" placeholder="Add labels" value={formData.labels} onChange={handleChange} />
              </FormField>

              <FormField label="Expected close date" tooltip="When you expect to close this deal.">
                <Input name="expectedCloseDate" type="date" value={formData.expectedCloseDate} onChange={handleChange} />
              </FormField>

              <FormField label="Owner">
                <Select
                  name="owner"
                  value={formData.owner}
                  onChange={handleChange}
                  options={[
                    { value: "current_user", label: "Anmol Garg (You)" },
                    { value: "other", label: "Other User" },
                  ]}
                />
              </FormField>

              <FormField label="Source channel">
                <Select
                  name="sourceChannel"
                  value={formData.sourceChannel}
                  onChange={handleChange}
                  placeholder="Select channel"
                  options={[
                    { value: "web", label: "Web" },
                    { value: "referral", label: "Referral" },
                  ]}
                />
              </FormField>

              <FormField label="Source channel ID">
                <Input name="sourceChannelId" placeholder="" value={formData.sourceChannelId} onChange={handleChange} />
              </FormField>
            </div>

            {/* RIGHT: Person Details */}
            <div className="w-1/2">
              <div className="border-t border-gray-300 pt-3 mb-4">
                <h3 className="text-xs font-bold text-gray-500 tracking-wider">PERSON</h3>
              </div>

              <FormField label="Phone">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input name="phone" placeholder="" value={formData.phone} onChange={handleChange} />
                  </div>
                  <div className="w-28">
                    <Select
                      name="phoneType"
                      value={formData.phoneType}
                      onChange={handleChange}
                      options={[
                        { value: "work", label: "Work" },
                        { value: "mobile", label: "Mobile" },
                      ]}
                    />
                  </div>
                </div>
                <button type="button" className="text-sm text-gray-600 hover:text-gray-800 mt-1 flex items-center">
                  <PlusIcon /> Add phone
                </button>
              </FormField>

              <FormField label="Email">
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Input name="email" type="email" placeholder="" value={formData.email} onChange={handleChange} />
                  </div>
                  <div className="w-28">
                    <Select
                      name="emailType"
                      value={formData.emailType}
                      onChange={handleChange}
                      options={[
                        { value: "work", label: "Work" },
                        { value: "personal", label: "Personal" },
                      ]}
                    />
                  </div>
                </div>
                <button type="button" className="text-sm text-gray-600 hover:text-gray-800 mt-1 flex items-center">
                  <PlusIcon /> Add email
                </button>
              </FormField>
            </div>
          </div>

          {error && <div className="text-red-500 text-sm mt-4 bg-red-50 p-2 rounded">{error}</div>}
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button type="button" className="text-sm text-gray-600 hover:text-gray-800 flex items-center">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path>
            </svg>
            Import
          </button>

          <div className="flex items-center space-x-4">
            <div className="flex items-center text-xs text-gray-500">
              <span className="font-medium text-gray-700">{formData.title.length}/12,500</span>
              <span className="ml-1 text-gray-400 cursor-help" title="Max title length"><InfoIcon /></span>
            </div>
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`px-4 py-2 text-sm font-medium text-white rounded shadow-sm ${
                isSubmitting ? "bg-green-400 cursor-not-allowed" : "bg-[#317e3e] hover:bg-[#286834]"
              }`}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddDeal;